import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AdminRoute } from "@/components/admin/AdminRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ThemeProvider } from "next-themes";

// Public pages
import AuthPage from "./pages/Auth";
import AuthCallbackPage from "./pages/AuthCallback";
import ResetPasswordPage from "./pages/ResetPassword";
import OnboardingPage from "./pages/Onboarding";
import LinkedInOnboardingPage from "./pages/LinkedInOnboarding";

// Protected pages
import FeedPage from "./pages/Feed";
import ChannelsPage from "./pages/Channels";
import ChannelFeedPage from "./pages/ChannelFeed";
import DirectoryPage from "./pages/Directory";
import EventsPage from "./pages/Events";
import MessagesPage from "./pages/Messages";
import ProfilePage from "./pages/Profile";
import MemberProfilePage from "./pages/MemberProfile";
import SettingsPage from "./pages/Settings";
import MatchupsPage from "./pages/Matchups";

// Admin pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCohorts from "./pages/admin/AdminCohorts";
import AdminChannels from "./pages/admin/AdminChannels";
import AdminOnboarding from "./pages/admin/AdminOnboarding";
import AdminInvites from "./pages/admin/AdminInvites";
import AdminModeration from "./pages/admin/AdminModeration";
import AdminMatchups from "./pages/admin/AdminMatchups";
import AdminMatching from "./pages/admin/AdminMatching";
import AdminAISettings from "./pages/admin/AdminAISettings";
import AdminReports from "./pages/admin/AdminReports";
import ApplicationPendingPage from "./pages/ApplicationPending";

import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function PasswordRecoveryRedirect({ children }: { children: React.ReactNode }) {
  const { isPasswordRecovery } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (isPasswordRecovery && location.pathname !== '/reset-password') {
      navigate('/reset-password', { replace: true });
    }
  }, [isPasswordRecovery, navigate, location.pathname]);

  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <PasswordRecoveryRedirect>
          <Routes>
            {/* Public routes */}
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/onboarding/linkedin" element={<LinkedInOnboardingPage />} />
            <Route path="/applications" element={<ApplicationPendingPage />} />

            {/* Protected routes with app layout */}
            <Route element={<AppLayout />}>
              <Route path="/home" element={<FeedPage />} />
              <Route path="/feed" element={<Navigate to="/home" replace />} />
              <Route path="/channels" element={<ChannelsPage />} />
              <Route path="/channels/:slug" element={<ChannelFeedPage />} />
              <Route path="/directory" element={<DirectoryPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/members/:userId" element={<MemberProfilePage />} />
              <Route path="/matchups" element={<MatchupsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* Legacy route redirect */}
              <Route path="/network" element={<Navigate to="/directory" replace />} />
            </Route>

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="matchups" element={<AdminMatchups />} />
              <Route path="matching" element={<AdminMatching />} />
              <Route path="settings/ai" element={<AdminAISettings />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="cohorts" element={<AdminCohorts />} />
              <Route path="channels" element={<AdminChannels />} />
              <Route path="onboarding" element={<AdminOnboarding />} />
              <Route path="invites" element={<AdminInvites />} />
              <Route path="moderation" element={<AdminModeration />} />
              <Route path="reports" element={<AdminReports />} />
            </Route>

            {/* Root — handles OAuth redirects and default navigation */}
            <Route path="/" element={<AuthCallbackPage />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </PasswordRecoveryRedirect>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </ThemeProvider>
  </ErrorBoundary>
);

export default App;
